// src/features/procedures/components/steps/FileUploadStep.jsx
import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTaskAttachments } from '../../../tasks/hooks/useTaskAttachments';
import { FileDropZone, useFilePicker, useUploadQueue, UploadProgressList } from '../../../files/upload';

/**
 * File Upload Step Component
 *
 * The file is uploaded as soon as it's picked or dropped on the step, attached
 * to the task, and stored as the step's answer. Type and size are checked first
 * and a refused file is explained right under the step.
 *
 * Validation Rules Config:
 * {
 *   "allowed_types": [         // Allowed MIME types
 *     "image/jpeg",
 *     "image/png",
 *     "image/webp",
 *     "application/pdf"
 *   ],
 *   "max_size_mb": 10,         // Maximum file size in megabytes
 *   "min_files": 1,            // Minimum number of files
 *   "max_files": 5             // Maximum number of files
 * }
 */
export default function FileUploadStep({ step, value, onChange, disabled, preview, procedureName, taskId }) {
  const { t } = useTranslation(['procedures', 'files', 'common']);
  const validationRules = step.validation_rules || {};

  const allowedTypes = useMemo(() => validationRules.allowed_types || [], [validationRules.allowed_types]);
  const accept = allowedTypes.length > 0 ? allowedTypes.join(',') : undefined;
  const maxSizeMB = validationRules.max_size_mb || 10;
  const maxFiles = validationRules.max_files || 1;
  const minFiles = validationRules.min_files || 1;

  const { error, uploadAndAttachFile, clearError } = useTaskAttachments();

  const uploads = useUploadQueue({
    accept,
    maxBytes: maxSizeMB * 1024 * 1024,
    tooLargeMessage: t('procedures:file_upload_step.too_large', { maxSizeMB }),
    upload: (file, { onUploadProgress }) => uploadAndAttachFile(file, taskId, procedureName, {
      attachmentLabel: `${procedureName} - ${step.label} - ${new Date().toLocaleString()}`,
      displayOrder: 0,
      onUploadProgress,
    }),
    // The step's answer is the uploaded file
    onUploaded: (result, file) => onChange({
      file_id: result.fileId,
      filename: file.name,
      filetype: file.type,
      file_size: file.size,
      uploaded_at: new Date().toISOString(),
    }),
  });
  const addFile = (files) => {
    clearError();
    uploads.enqueue(files.slice(0, 1));
  };
  const picker = useFilePicker({ onFiles: addFile, accept, multiple: false });

  if (preview) {
    return (
      <Box>
        <Alert severity="info" icon={<UploadIcon />}>
          <Typography variant="body2">
            {step.label}
          </Typography>
          <Typography variant="caption" display="block">
            {t('procedures:file_upload_step.files_spec', { count: minFiles === maxFiles ? minFiles : `${minFiles}-${maxFiles}`, maxSizeMB })}
          </Typography>
        </Alert>
      </Box>
    );
  }

  const fileData = value;
  const hasFile = fileData && typeof fileData === 'object' && fileData.file_id;
  const canUpload = !disabled && Boolean(taskId);

  return (
    <FileDropZone
      onFiles={addFile}
      multiple={false}
      disabled={!canUpload || uploads.busy}
      label={hasFile ? t('procedures:file_upload_step.drop_replace') : t('files:upload.drop_file')}
      sx={{ borderRadius: 1 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {hasFile && (
          <Chip
            icon={<FileIcon />}
            label={fileData.filename}
            color="success"
            variant="outlined"
            sx={{ maxWidth: '100%' }}
          />
        )}
        {/* Nothing to replace once the procedure is closed */}
        {!(hasFile && disabled) && (
          <Button
            variant={hasFile ? 'text' : 'outlined'}
            startIcon={<UploadIcon />}
            disabled={!canUpload || uploads.busy}
            fullWidth={!hasFile}
            onClick={picker.open}
          >
            {hasFile
              ? t('procedures:file_upload_step.replace_file', 'Erstat fil')
              : t('procedures:file_upload_step.choose_file', 'Vælg fil')}
          </Button>
        )}
        {picker.input}
      </Box>

      <UploadProgressList items={uploads.items} onDismiss={uploads.dismiss} sx={{ mt: 1.5 }} />
      {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={clearError}>{error}</Alert>}

      {!hasFile && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {canUpload && `${t('procedures:file_upload_step.drop_hint')} · `}
          {t('procedures:file_upload_step.max_size', { defaultValue: 'Max' })}: {maxSizeMB} MB
          {maxFiles > 1 && ` • ${t('procedures:file_upload_step.max_files', { defaultValue: 'Up to {{count}} files', count: maxFiles })}`}
        </Typography>
      )}
    </FileDropZone>
  );
}
